<template>
  <div>
    <!-- YMME Steps -->
    <v-stepper v-model="step" class="elevation-0 no-transition py-3">
      <v-stepper-header class="elevation-0">
        <template>
          <div v-for="(ymmeStep, index) in ymmeSteps" :key="`step-${index}`">
            <v-stepper-step :complete="step > ymmeStep.id" :step="ymmeStep.id" class="py-0">
              {{ ymmeStep.label }}
              <small v-html="ymmeStep.model"></small>
            </v-stepper-step>
          </div>
        </template>
      </v-stepper-header>
    </v-stepper>

    <v-layout row wrap>
      <v-flex grow>
        <v-slide-y-reverse-transition group appear leave-absolute hide-on-leave>
          <v-card flat v-if="loader" :key="0" class="text-xs-center pa-4 ma-4">
            <v-progress-circular :size="100" indeterminate class="base--text">
              {{ $t('LoadingSpinner.DefaultMessage') }}
            </v-progress-circular>
          </v-card>

          <!--YEARS LIST-->
          <v-card flat v-else-if="step === 1" :key="1">
            <h2 class="headline my-3 text-xs-center hidden-md-and-up">{{ $t('Vehicle.Labels.Year') }}</h2>
            <v-divider></v-divider>
            <ul class="columns cols-6">
              <v-slide-y-reverse-transition group appear leave-absolute hide-on-leave>
                <li v-for="year in years" :key="`year-${year.id}`" class="text-xs-center">
                  <v-btn class="ma-0 full-width caption" flat @click="setYear(year)">{{ year.value }}</v-btn>
                </li>
              </v-slide-y-reverse-transition>
            </ul>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn :disabled="ymme.year === null" large depressed class="white--text" @click="stepForward">{{
              $t('Vehicle.Actions.Next')
              }}</v-btn>
            </v-card-actions>
          </v-card>

          <!--MAKES LIST-->
          <v-card flat v-else-if="step === 2" :key="2">
            <h2 class="headline my-3 text-xs-center hidden-md-and-up">{{ $t('Vehicle.Labels.Make') }}</h2>
            <v-divider></v-divider>

            <v-layout row wrap fill-height>
              <v-flex shrink justify-center align-center d-flex pa-2>
                <v-btn-toggle mandatory v-model="makeType" class="transparent elevation-0">
                  <v-tooltip bottom>
                    <v-btn disabled slot="activator" flat value="car-truck" class="text-capitalize caption">
                      <v-icon left>directions_car</v-icon>
                      <span class="hidden-sm-and-down">{{ $t('Vehicle.Actions.CarTrucks') }}</span>
                    </v-btn>

                    <span>{{ $t('Vehicle.Actions.CarTrucks') }}</span>
                  </v-tooltip>
                </v-btn-toggle>
              </v-flex>

              <v-flex shrink d-flex>
                <v-divider vertical></v-divider>
              </v-flex>

              <v-flex grow d-flex>
                <v-text-field flat solo clearable hide-details :label="$t('Vehicle.Labels.FilterMakes')"
                  append-icon="search" v-model="search"></v-text-field>
              </v-flex>
            </v-layout>

            <v-divider></v-divider>

            <ul class="columns align-left cols-4">
              <li v-for="make in filteredMakes" :key="`make-${make.id}`" class="text-xs-left">
                <v-btn style="text-align: left" class="ma-0 full-width caption text-xs-left" small flat
                  @click="setMake(make)">{{ make.value }}</v-btn>
              </li>
            </ul>

            <v-divider></v-divider>

            <v-card-actions>
              <v-btn dark large depressed color="orange darken1" @click="stepBack">{{ $t('Vehicle.Actions.Back') }}
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn :disabled="ymme.make === null" large depressed class="white--text" @click="stepForward">
                {{ $t('Vehicle.Actions.Next') }}
              </v-btn>
            </v-card-actions>
          </v-card>

          <!--MODELS LIST-->
          <v-card flat v-else-if="step === 3" :key="3">
            <h2 class="headline my-3 text-xs-center hidden-md-and-up">{{ $t('Vehicle.Labels.Model') }}</h2>

            <v-divider></v-divider>

            <ul class="columns" :class="{ 'columns': models.length > 1, 'cols-2': models.length >= 4 }">
              <li v-for="model in models" :key="`model-${model.id}`">
                <v-btn class="ma-0 full-width caption" small flat @click="setModel(model)">{{ model.value }}</v-btn>
              </li>
            </ul>

            <v-divider></v-divider>

            <v-card-actions>
              <v-btn dark large depressed color="orange darken1" @click="stepBack">{{ $t('Vehicle.Actions.Back') }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn :disabled="ymme.model === null" large depressed class="white--text" @click="stepForward">{{
              $t('Vehicle.Actions.Next')
              }}</v-btn>
            </v-card-actions>
          </v-card>

          <!--ENGINES LIST-->
          <v-card flat v-else :key="4">
            <h2 class="headline my-3 text-xs-center hidden-md-and-up">{{ $t('Vehicle.Labels.Engine') }}</h2>

            <v-divider></v-divider>

            <ul class="columns" :class="{ 'cols-2': engines.length >= 4 }">
              <li v-for="engine in engines" :key="`engine-${engine.id}`" class="text-xs-center">
                <v-btn class="ma-0 full-width caption" flat @click="setEngine(engine)">{{
                engine.value
                }}</v-btn>
              </li>
            </ul>

            <v-divider></v-divider>

            <v-card-actions>
              <v-btn dark large depressed color="orange darken1" @click="stepBack">{{ $t('Vehicle.Actions.Back') }}
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn :disabled="ymme.engine === null" large depressed class="white--text"
                :to="{ name: 'parts-lookup', params: { provider: this.skin.provider } }">{{
                $t('Vehicle.Actions.BrowseParts')
                }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-slide-y-reverse-transition>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  name: "NewVehicle",
  data() {
    return {
      loader: false,
      step: 1,
      search: "",
      makeType: "car-truck",
      ymme: {
        year: null,
        make: null,
        makeCode: null,
        model: null,
        modelCode: null,
        engine: null,
        engineCode: null
      },
    }
  },
  computed: {
    ...mapState({
      years: state => state.vehicle.years,
      makes: state => state.vehicle.makes,
      models: state => state.vehicle.models,
      engines: state => state.vehicle.engines,
      minYears: state => state.vehicle.minYears,
      maxYears: state => state.vehicle.maxYears,
      skin: state => state.settings.skin,
    }),
    ymmeSteps() {
      return [
        {
          id: 1,
          name: "year",
          label: this.$i18n.tc("Vehicle.Labels.Year"),
          model: this.ymme.year
        },
        {
          id: 2,
          name: "make",
          label: this.$i18n.tc("Vehicle.Labels.Make"),
          model: this.ymme.make
        },
        {
          id: 3,
          name: "model",
          label: this.$i18n.tc("Vehicle.Labels.Model"),
          model: this.ymme.model
        },
        {
          id: 4,
          name: "engine",
          label: this.$i18n.tc("Vehicle.Labels.Engine"),
          model: this.ymme.engine
        }
      ];
    },
    filteredMakes() {
      return this.makes.filter(make =>
        make.value.toLowerCase()
          .includes(this.search.toLowerCase()))
    },
    watchSetOneEngine() {
      return this.setOneEngine()
    }
  },
  async mounted() {
    this.loader = true;
    if (!this.years.length)
      await this.GET_YMME_DATA({ service: "years" });

    this.loader = false;
  },
  methods: {
    ...mapActions({
      GET_YMME_DATA: "vehicle/GET_YMME_DATA",
      CREATE_VEHICLE: "vehicle/CREATE_VEHICLE",
      UPDATE_STATE: "vehicle/UPDATE_STATE"
    }),
    async setYear(year) {
      this.loader = true;
      this.ymme.year = year.id;

      await this.GET_YMME_DATA({
        service: "makes",
        params: {
          year: this.ymme.year,
        }
      });

      this.stepForward()
    },
    async setMake(make) {
      this.loader = true;
      this.ymme.makeCode = make.id;
      this.ymme.make = make.value;

      await this.GET_YMME_DATA({
        service: "models",
        params: {
          year: this.ymme.year,
          make: this.ymme.makeCode,
        }
      });

      this.stepForward()
    },
    async setModel(model) {
      this.loader = true;
      this.ymme.modelCode = model.id;
      this.ymme.model = model.value;
      await this.GET_YMME_DATA({
        service: "engines",
        params: {
          year: this.ymme.year,
          make: this.ymme.makeCode,
          model: this.ymme.modelCode,
        }
      });

      if (this.engines.length == 1)
        await this.setEngine(this.engines[0])
      else
        this.stepForward();
    },
    async setEngine(engine) {
      this.loader = true;
      this.ymme.engineCode = engine.id;
      this.ymme.engine = engine.value;

      await this.CREATE_VEHICLE({ vehicle: this.ymme });

      this.$router.push({ name: "parts-lookup", params: { provider: this.skin.provider } });
      this.step = 4;
      this.loader = false;

      Object.assign(this.$data, this.$options.data.apply(this));
    },
    stepBack() {
      this.step = this.step - 1;
    },
    stepForward() {
      this.step = parseInt(this.step) + 1;
      this.loader = false;
    },
    resetStepper() {
      this.step = 1;
    }
  }
};
</script>

<style lang="stylus" scoped>
:deep() {
  .v-btn-toggle .v-btn {
    opacity: 0.7;
  }

  .makes .v-btn__content {
    justify-content: left !important;
  }
}
</style>